import React, { useState } from 'react';
import './UserIntentPopup.css';
import { useTranslation } from 'react-i18next';

export type UserIntent = 'speed' | 'quality' | 'strength' | 'balanced' | 'custom';

interface IntentPreset {
  id: UserIntent;
  title: string;
  description: string;
  benefits: string[];
  tradeoffs: string[];
}

interface UserIntentPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (intent: UserIntent, customPrompt?: string, aiSuggestion?: any) => void;
}

export function UserIntentPopup({ isOpen, onClose, onSubmit }: UserIntentPopupProps) {
  const { t } = useTranslation();
  const [selectedIntent, setSelectedIntent] = useState<UserIntent>('balanced');
  const [customPrompt, setCustomPrompt] = useState('');
  const [isProcessingPrompt, setIsProcessingPrompt] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<{
    intent: UserIntent, 
    explanation: string,
    optimization_strategy?: string,
    user_friendly_summary?: string,
    custom_parameters?: Array<{parameter: string, value: string, reasoning: string}>,
    expected_benefits?: string[],
    trade_offs?: string[]
  } | null>(null);
  const [promptError, setPromptError] = useState<string | null>(null);

  const intentPresets: IntentPreset[] = [
    {
      id: 'speed',
      title: t('userIntent.presets.speed.title'),
      description: t('userIntent.presets.speed.description'),
      benefits: [
        t('userIntent.presets.speed.benefits.fasterPrinting'),
        t('userIntent.presets.speed.benefits.higherLayers'),
        t('userIntent.presets.speed.benefits.fasterSpeeds')
      ],
      tradeoffs: [
        t('userIntent.presets.speed.tradeoffs.lowerQuality'),
        t('userIntent.presets.speed.tradeoffs.visibleLines')
      ],
    },
    {
      id: 'quality',
      title: t('userIntent.presets.quality.title'),
      description: t('userIntent.presets.quality.description'),
      benefits: [
        t('userIntent.presets.quality.benefits.finestDetails'),
        t('userIntent.presets.quality.benefits.smoothSurfaces'),
        t('userIntent.presets.quality.benefits.minimalLines')
      ],
      tradeoffs: [
        t('userIntent.presets.quality.tradeoffs.longerTime'),
        t('userIntent.presets.quality.tradeoffs.moreMaterial')
      ],
    },
    {
      id: 'strength',
      title: t('userIntent.presets.strength.title'),
      description: t('userIntent.presets.strength.description'),
      benefits: [
        t('userIntent.presets.strength.benefits.strongerParts'),
        t('userIntent.presets.strength.benefits.betterAdhesion'),
        t('userIntent.presets.strength.benefits.higherInfill')
      ],
      tradeoffs: [
        t('userIntent.presets.strength.tradeoffs.longerPrint'),
        t('userIntent.presets.strength.tradeoffs.moreMaterial')
      ],
    },
    {
      id: 'balanced',
      title: t('userIntent.presets.balanced.title'),
      description: t('userIntent.presets.balanced.description'),
      benefits: [
        t('userIntent.presets.balanced.benefits.balancedSpeedQuality'),
        t('userIntent.presets.balanced.benefits.reliableResults'),
        t('userIntent.presets.balanced.benefits.moderateUse')
      ],
      tradeoffs: [
        t('userIntent.presets.balanced.tradeoffs.notOptimized')
      ],
    }
  ];

  const handlePresetSelect = (intent: UserIntent) => {
    setSelectedIntent(intent);
    setAiSuggestion(null);
    setPromptError(null);
  };

  const handleAIPrompt = async () => {
    if (!customPrompt.trim() || isProcessingPrompt) return;
    
    setIsProcessingPrompt(true);
    setPromptError(null);
    
    try {
      // Determine the base URL for API calls
      const baseUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:8001' 
        : '';
      
      const response = await fetch(`${baseUrl}/api/v1/ai/analyze-print-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: customPrompt.trim()
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setAiSuggestion({
          intent: data.suggested_intent || 'custom', // Use 'custom' when no preset selected
          explanation: data.explanation,
          optimization_strategy: data.optimization_strategy,
          user_friendly_summary: data.user_friendly_summary,
          custom_parameters: data.custom_parameters,
          expected_benefits: data.expected_benefits,
          trade_offs: data.trade_offs
        });
        // Only set preset if AI actually suggests one, otherwise keep current selection
        if (data.suggested_intent) {
          setSelectedIntent(data.suggested_intent);
        }
        // If no preset suggested, we're using pure custom optimization
      } else {
        setPromptError(data.error || t('userIntent.errors.analysisFailedRetry'));
      }
      
    } catch (error) {
      console.error('AI prompt analysis failed:', error);
      setPromptError(error instanceof Error ? error.message : t('userIntent.errors.analysisFailedConnection'));
    } finally {
      setIsProcessingPrompt(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAIPrompt();
    }
  };

  const handleSubmit = () => {
    // If we have an AI suggestion with custom parameters, pass the full AI configuration
    if (aiSuggestion && aiSuggestion.custom_parameters) {
      onSubmit(aiSuggestion.intent, customPrompt, aiSuggestion);
    } else {
      onSubmit(selectedIntent, aiSuggestion ? customPrompt : undefined);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="user-intent-popup-overlay" onClick={onClose}>
      <div className="user-intent-popup" onClick={(e) => e.stopPropagation()}>
        <div className="user-intent-header">
          <div className="user-intent-header-content">
            <h2>{t('userIntent.title')}</h2>
          </div>
        </div>
        
        <div className="user-intent-content">
        {/* Preset Selection Section */}
        <div className="preset-section">
          <h3>{t('userIntent.chooseGoal')}</h3>
          <div className="preset-grid">
            {intentPresets.map((preset) => (
              <div 
                key={preset.id}
                className={`preset-card ${selectedIntent === preset.id ? 'selected' : ''}`}
                onClick={() => handlePresetSelect(preset.id)}
              >
                <h4>{preset.title}</h4>
                <p>
                  {preset.description.split('\n').map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      {index < preset.description.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
                <div className="preset-benefits">
                  {preset.benefits.map((benefit, index) => (
                    <div key={index} className="benefit-item">
                      <span className="check"></span>
                      {benefit}
                    </div>
                  ))}
                </div>
                <div className="preset-tradeoffs">
                  {preset.tradeoffs.map((tradeoff, index) => (
                    <div key={index} className="tradeoff-item">
                      <span className="warning">⚠</span>
                      {tradeoff}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Chat Section */}
        <div className="ai-chat-section">
          <h3>{t('userIntent.orDescribe')}</h3>
          <p className="ai-chat-description">
            {t('userIntent.describeNeeds')}
          </p>
          
          {aiSuggestion && (
            <div className="ai-suggestion">
              <div className="suggestion-header">
                <span className="ai-icon">🤖</span>
                <strong>{t('userIntent.aiCustomOptimization')}</strong>
              </div>
              
              <div className="ai-bullet-summary">
                {aiSuggestion.user_friendly_summary && (
                  <div className="ai-bullet-point">
                    <span className="bullet">•</span>
                    <span>{aiSuggestion.user_friendly_summary}</span>
                  </div>
                )}
                
                {aiSuggestion.expected_benefits && aiSuggestion.expected_benefits.map((benefit, index) => (
                  <div key={index} className="ai-bullet-point benefit">
                    <span className="bullet green">✓</span>
                    <span>{benefit}</span>
                  </div>
                ))}
                
                {aiSuggestion.trade_offs && aiSuggestion.trade_offs.map((tradeoff, index) => (
                  <div key={index} className="ai-bullet-point tradeoff">
                    <span className="bullet orange">⚠</span>
                    <span>{tradeoff}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {promptError && (
            <div className="prompt-error">
              <span className="error-icon">⚠</span>
              {promptError}
            </div>
          )}
          
          <div className="ai-input-container">
            <div className="ai-input-wrapper">
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('userIntent.placeholder')}
                className="ai-prompt-textarea"
                maxLength={500}
                rows={3}
                disabled={isProcessingPrompt}
              />
              <button 
                onClick={handleAIPrompt}
                className={`ai-submit-button ${customPrompt.trim() ? 'enabled' : ''}`}
                disabled={!customPrompt.trim() || isProcessingPrompt}
                title={t('userIntent.analyzeWithAI')}
              >
                {isProcessingPrompt ? (
                  <div className="spinner-small"></div>
                ) : (
                  <img src="/right_arrow.svg" alt="Submit" className="arrow-icon" />
                )}
              </button>
            </div>
          </div>
          
          <div className="ai-examples">
            <p><strong>{t('userIntent.examplesTitle')}</strong></p>
            <ul>
              <li>"{t('userIntent.example1')}"</li>
              <li>"{t('userIntent.example2')}"</li>
            </ul>
          </div>
        </div>
        
        <div className="popup-buttons">
          <button className="cancel-button" onClick={onClose}>
            {t('buttons.cancel')}
          </button>
          <button 
            className="apply-button" 
            onClick={handleSubmit}
          >
            {t('userIntent.nextPrinterSettings')}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
} 